import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ConstructionTypeIcon } from 'components/ConstructionTypeIcon';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { Button, FieldLabel, Grid, GridItem, cnConsta, cnMixSpace } from 'uikit';
import { destroyAgGridTableFilters } from 'utils';

import type { GridApi } from 'ag-grid-community';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Styles from '../catalogConstructionTypes.module.css';
import {
    CODE_PATTERN,
    CONSTRUCTION_TYPE_NAME_FIELDS,
    UNIQUE_CODE_ERROR,
    UNIQUE_NAME_ERROR,
    buildCreateConstructionTypeInput,
    buildUpdateConstructionTypeInput,
    getConstructionTypeFormValues,
    trimValue,
} from '../helpers';
import { useConstructionTypeCodeUniqueness } from '../hooks/useConstructionTypeCodeUniqueness';
import { useConstructionTypeIsUsedInProjects } from '../hooks/useConstructionTypeIsUsedInProjects';
import { useConstructionTypeNamesUniqueness } from '../hooks/useConstructionTypeNamesUniqueness';
import { useCreateConstructionType } from '../hooks/useCreateConstructionType';
import { useUpdateConstructionType } from '../hooks/useUpdateConstructionType';
import { SelectConstructionTypeIconModal } from '../SelectConstructionTypeIconModal';
import type { IConstructionTypeFormValues, TCatalogConstructionTypeRow } from '../types';

const cnTextField = cnConsta('TextField');

const nameFieldRules = {
    required: 'Обязательное поле',
    maxLength: {
        value: 255,
        message: 'Максимальное количество символов – 255',
    },
};

const codeFieldRules = {
    ...nameFieldRules,
    pattern: {
        value: CODE_PATTERN,
        message: 'Допустимы только английские буквы',
    },
};

export const CreateOrEditConstructionTypeModal = ({
    isModalOpened,
    closeHandler,
    isCreating = false,
    dataEditModal,
    idCatalog,
    refreshData,
    gridRefApi,
    setFilterValues,
}: {
    isModalOpened: boolean;
    closeHandler: (state: boolean) => void;
    isCreating?: boolean;
    dataEditModal: TCatalogConstructionTypeRow | null;
    idCatalog?: number;
    refreshData: () => void;
    gridRefApi: GridApi | undefined;
    setFilterValues: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
}) => {
    const dispatch = useDispatch();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isUsageConfirmOpen, setIsUsageConfirmOpen] = useState(false);
    const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
    const [isIconModalOpen, setIsIconModalOpen] = useState(false);
    const [valuesToSave, setValuesToSave] = useState<IConstructionTypeFormValues | null>(null);

    const editRow = isCreating ? null : dataEditModal;
    const initialValues = useMemo(() => getConstructionTypeFormValues(editRow), [editRow]);

    const { control, handleSubmit, reset, watch, setValue, setError, clearErrors, formState } =
        useForm<IConstructionTypeFormValues>({
            mode: 'onChange',
            criteriaMode: 'all',
            defaultValues: initialValues,
        });

    const values = watch();

    useEffect(() => {
        reset(initialValues);
    }, [initialValues, reset]);

    const { notUniqueFields, isAllUnique } = useConstructionTypeNamesUniqueness({
        values,
        initialValues,
        catalogConstructionTypeId: editRow?.id,
    });

    const { isCodeNotUnique, isCodeUnique } = useConstructionTypeCodeUniqueness({
        code: values.code,
        isEnabled: isCreating,
    });

    const { errors } = formState;

    useEffect(() => {
        const hasUniqueError = errors.code?.message === UNIQUE_CODE_ERROR;

        if (isCodeNotUnique && !hasUniqueError) {
            setError('code', { type: 'manual', message: UNIQUE_CODE_ERROR });
        } else if (!isCodeNotUnique && hasUniqueError) {
            clearErrors('code');
        }
    }, [clearErrors, errors.code?.message, isCodeNotUnique, setError]);

    useEffect(() => {
        CONSTRUCTION_TYPE_NAME_FIELDS.forEach(({ field }) => {
            const isNotUnique = notUniqueFields.includes(field);
            const hasUniqueError = errors[field]?.message === UNIQUE_NAME_ERROR;

            if (isNotUnique && !hasUniqueError) {
                setError(field, { type: 'manual', message: UNIQUE_NAME_ERROR });
            } else if (!isNotUnique && hasUniqueError) {
                clearErrors(field);
            }
        });
    }, [clearErrors, errors, notUniqueFields, setError]);

    const onSuccessHandler = () => {
        refreshData();
        destroyAgGridTableFilters({
            api: gridRefApi,
            columns: ['name'],
        });
        setFilterValues({});
    };

    const {
        resetEditData,
        constructionTypeEditData,
        constructionTypeEditStatus,
        constructionTypeEditError,
        constructionTypeEditRequest,
        isSavingInProgress,
    } = useUpdateConstructionType({
        onSuccessHandler,
    });

    const {
        createConstructionTypeStatus,
        createConstructionTypeError,
        createConstructionTypeRequest,
        isCreatingInProgress,
        createConstructionTypeData,
        resetCreatingData,
    } = useCreateConstructionType({
        onSuccessHandler,
    });

    const createOrEditStatus = isCreating ? createConstructionTypeStatus : constructionTypeEditStatus;
    const createOrEditError = isCreating ? createConstructionTypeError : constructionTypeEditError;
    const createOrEditData = isCreating ? createConstructionTypeData : constructionTypeEditData;
    const { checkUsage, isCheckingUsage, isUsageCheckError, usageStatus, usageError, handleCloseUsageError } =
        useConstructionTypeIsUsedInProjects();

    const submitUpdate = (formValues: IConstructionTypeFormValues) => {
        if (!idCatalog || !editRow) {
            return;
        }

        dispatch(
            constructionTypeEditRequest({
                input: buildUpdateConstructionTypeInput({
                    values: formValues,
                    row: editRow,
                    catalogId: idCatalog,
                }),
            }),
        );
        setIsSubmitted(true);
    };

    const isCommit = isCreatingInProgress || isSavingInProgress || isCheckingUsage;

    useEffect(() => {
        if (isSubmitted && createOrEditData?.result) {
            resetCreatingData(null);
            resetEditData();
            closeHandler(false);
        }
    }, [closeHandler, createOrEditData?.result, isSubmitted, resetCreatingData, resetEditData]);

    const onSubmit = (formValues: IConstructionTypeFormValues) => {
        if (!isAllUnique || !isCodeUnique) {
            return;
        }

        if (isCreating) {
            if (!idCatalog) {
                return;
            }

            dispatch(
                createConstructionTypeRequest({
                    input: buildCreateConstructionTypeInput({
                        values: formValues,
                        catalogId: idCatalog,
                    }),
                }),
            );
            setIsSubmitted(true);

            return;
        }

        if (!editRow?.code) {
            return;
        }

        checkUsage(editRow.code, (isUsed) => {
            if (isUsed) {
                setValuesToSave(formValues);
                setIsUsageConfirmOpen(true);

                return;
            }

            submitUpdate(formValues);
        });
    };

    const handleConfirmUsage = () => {
        if (!valuesToSave) {
            return;
        }

        submitUpdate(valuesToSave);
    };

    const handleUsageDialogClose = (confirmed: boolean) => {
        setIsUsageConfirmOpen(false);

        if (!confirmed) {
            setValuesToSave(null);
        }
    };

    const hasChanges =
        trimValue(values.code) !== initialValues.code ||
        values.iconCode !== initialValues.iconCode ||
        CONSTRUCTION_TYPE_NAME_FIELDS.some(({ field }) => trimValue(values[field]) !== initialValues[field]);

    const handleCancel = () => {
        if (hasChanges) {
            setIsCancelConfirmOpen(true);

            return;
        }

        closeHandler(false);
    };

    const handleIconSave = (iconCode: string) => {
        setValue('iconCode', iconCode);
        setIsIconModalOpen(false);
    };

    const onCloseSaveErrorModal = () => {
        setIsSubmitted(false);
    };

    const title = isCreating ? 'Создание вида строительства' : 'Редактирование вида строительства';
    const btnConfirmLabel = isCreating ? 'Создать' : 'Сохранить';

    const hasErrors = Object.keys(errors).length > 0;
    const btnConfirmDisabled = !idCatalog || !isAllUnique || !isCodeUnique || hasErrors || (!isCreating && !hasChanges);

    return (
        <ModalDialog
            isOpen={isModalOpened}
            title={title}
            onClose={handleCancel}
            onConfirm={handleSubmit(onSubmit)}
            withTitleCloseButton
            style={{ maxWidth: '520px', width: '100%' }}
            isCommit={isCommit}
            isOnClickOutsideDisable
            btnConfirmLabel={btnConfirmLabel}
            btnConfirmDisabled={btnConfirmDisabled}
        >
            <div className={cnMixSpace({ m: 'l' })}>
                <Grid rowGap="s" cols="1">
                    <GridItem>
                        <ControlledTextField
                            name="code"
                            label="Код"
                            control={control}
                            type="text"
                            directionTooltip="rightCenter"
                            tooltipSize="m"
                            labelPosition="left"
                            rules={isCreating ? codeFieldRules : undefined}
                            required={isCreating}
                            disabled={!isCreating}
                            className={Styles.formField}
                        />
                    </GridItem>
                    {CONSTRUCTION_TYPE_NAME_FIELDS.map(({ field, label }) => (
                        <GridItem key={field}>
                            <ControlledTextField
                                name={field}
                                label={label}
                                control={control}
                                type="text"
                                directionTooltip="rightCenter"
                                tooltipSize="m"
                                labelPosition="left"
                                rules={nameFieldRules}
                                required
                                className={Styles.formField}
                            />
                        </GridItem>
                    ))}
                    <GridItem>
                        <div
                            className={cn(
                                cnTextField({
                                    labelPosition: 'left',
                                    size: 's',
                                    view: 'default',
                                    width: 'full',
                                    lineHeight: 'm',
                                }),
                                Styles.formField,
                                Styles.iconField,
                            )}
                        >
                            <FieldLabel size="s" className={cn(cnTextField('Label', ['TextField-Label']))}>
                                Иконка
                            </FieldLabel>
                            <div className={Styles.iconValue}>
                                <ConstructionTypeIcon iconCode={values.iconCode} size={32} />
                                <Button
                                    label="Выбрать"
                                    size="s"
                                    view="ghost"
                                    onClick={() => setIsIconModalOpen(true)}
                                />
                            </div>
                        </div>
                    </GridItem>
                </Grid>
            </div>

            {isIconModalOpen && (
                <SelectConstructionTypeIconModal
                    isOpen={isIconModalOpen}
                    onClose={() => setIsIconModalOpen(false)}
                    onSave={handleIconSave}
                />
            )}

            <ErrorHandlingModal
                titleText={title}
                customButtonText="Понятно"
                viewButton="primary"
                isSubmitted={isSubmitted}
                statusRequest={createOrEditStatus}
                dataRequest={createOrEditData}
                errorsRequest={createOrEditError}
                handleCloseModalError={onCloseSaveErrorModal}
                style={{ maxWidth: '520px' }}
            />

            <ErrorHandlingModal
                titleText={title}
                customButtonText="Понятно"
                viewButton="primary"
                isSubmitted={isUsageCheckError}
                statusRequest={usageStatus}
                errorsRequest={usageError}
                handleCloseModalError={handleCloseUsageError}
                style={{ maxWidth: '520px' }}
            />

            <ConfirmationDialog
                textHeader="Редактирование записи"
                textConfirmation="Внесение изменений в наименование вида строительства затронет все проекты, в которых используется данный вид строительства. Вы уверены, что хотите продолжить?"
                cancelButtonText="Отменить"
                confirmButtonText="Подтвердить"
                isOpened={isUsageConfirmOpen}
                onSubmit={handleConfirmUsage}
                onClose={handleUsageDialogClose}
            />

            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Введенные данные не будут сохранены."
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isCancelConfirmOpen}
                onSubmit={() => closeHandler(false)}
                onClose={() => setIsCancelConfirmOpen(false)}
            />
        </ModalDialog>
    );
};

CreateOrEditConstructionTypeModal.displayName = 'CreateOrEditConstructionTypeModal';
